import {FormattedMessage, defineMessages} from 'react-intl';
import {view} from '@sail/ui';
import {useAnalytics} from '@stripe-internal/inline-data';
import {useEffect} from 'react';
import {Payout} from './MerchantPayoutStatus';

// NOTE: this is mostly adapted from what is used in Dashboard, however we have changed the copy slightly
// to not reference contacting Stripe Support, since the user is already trying to do that.
// https://livegrep.corp.stripe.com/view/stripe-internal/pay-server/manage/frontend/src/payouts/detail/PayoutDetailTimelineErrorMessages.tsx

type TimelineErrorMessageKeys =
  | 'account_closed'
  | 'account_frozen'
  | 'account_frozen_no_phone'
  | 'bank_account_restricted'
  | 'bank_account_restricted_no_phone'
  | 'bank_ownership_changed'
  | 'bank_ownership_changed_no_phone'
  | 'could_not_process'
  | 'could_not_process_no_phone'
  | 'debit_not_authorized'
  | 'debit_not_authorized_no_phone'
  | 'declined'
  | 'declined_no_phone'
  | 'insufficient_funds'
  | 'invalid_account_number'
  | 'incorrect_account_holder_name'
  | 'incorrect_account_holder_address'
  | 'incorrect_account_holder_tax_id'
  | 'invalid_currency'
  | 'no_account'
  | 'stripe_could_not_process'
  | 'unsupported_card'
  | 'unknown';

const messages = defineMessages({
  account_closed: {
    id: 'mse_inline_data.merchant_payout_status.error.account_closed',
    description: 'Timeline message for account_closed payout error.',
    defaultMessage:
      'Connect a new account to Stripe to start receiving payouts again.',
  },
  account_frozen: {
    id: 'mse_inline_data.merchant_payout_status.error.account_frozen',
    description: 'Timeline message for account_frozen payout error.',
    defaultMessage:
      'Contact your bank at {phone} to start receiving payouts again.',
  },
  account_frozen_no_phone: {
    id: 'mse_inline_data.merchant_payout_status.error.account_frozen_no_phone',
    description: 'Timeline message for account_frozen payout error.',
    defaultMessage: 'Contact your bank to start receiving payouts again.',
  },
  bank_account_restricted: {
    id: 'mse_inline_data.merchant_payout_status.error.bank_account_restricted',
    description: 'Timeline message for bank_account_restricted payout error.',
    defaultMessage:
      'Contact your bank at {phone} to start receiving payouts again.',
  },
  bank_account_restricted_no_phone: {
    id: 'mse_inline_data.merchant_payout_status.error.bank_account_restricted_no_phone',
    description: 'Timeline message for bank_account_restricted payout error.',
    defaultMessage: 'Contact your bank to start receiving payouts again.',
  },
  bank_ownership_changed: {
    id: 'mse_inline_data.merchant_payout_status.error.bank_ownership_changed',
    description: 'Timeline message for bank_ownership_changed payout error.',
    defaultMessage:
      'Contact your bank at {phone} to start receiving payouts again.',
  },
  bank_ownership_changed_no_phone: {
    id: 'mse_inline_data.merchant_payout_status.error.bank_ownership_changed_no_phone',
    description: 'Timeline message for bank_ownership_changed payout error.',
    defaultMessage: 'Contact your bank to start receiving payouts again.',
  },
  could_not_process: {
    id: 'mse_inline_data.merchant_payout_status.error.could_not_process',
    description: 'Timeline message for could_not_process payout error.',
    defaultMessage:
      'Contact your bank at {phone} to start receiving payouts again.',
  },
  could_not_process_no_phone: {
    id: 'mse_inline_data.merchant_payout_status.error.could_not_process_no_phone',
    description: 'Timeline message for could_not_process payout error.',
    defaultMessage: 'Contact your bank to start receiving payouts again.',
  },
  debit_not_authorized: {
    id: 'mse_inline_data.merchant_payout_status.error.debit_not_authorized',
    description: 'Timeline message for debit_not_authorized payout error.',
    defaultMessage:
      'Contact your bank at {phone} to set up debit transactions.',
  },
  debit_not_authorized_no_phone: {
    id: 'mse_inline_data.merchant_payout_status.error.debit_not_authorized_no_phone',
    description: 'Timeline message for debit_not_authorized payout error.',
    defaultMessage: 'Contact your bank to set up debit transactions.',
  },
  declined: {
    id: 'mse_inline_data.merchant_payout_status.error.declined',
    description: 'Timeline message for declined payout error.',
    defaultMessage:
      'Contact your bank at {phone} to start receiving payouts again.',
  },
  declined_no_phone: {
    id: 'mse_inline_data.merchant_payout_status.error.declined_no_phone',
    description: 'Timeline message for declined payout error.',
    defaultMessage: 'Contact your bank to start receiving payouts again.',
  },
  insufficient_funds: {
    id: 'mse_inline_data.merchant_payout_status.error.insufficient_funds',
    description: 'Timeline message for insufficient_funds payout error.',
    defaultMessage: 'Contact your bank to start receiving payouts again.',
  },
  invalid_account_number: {
    id: 'mse_inline_data.merchant_payout_status.error.invalid_account_number',
    description: 'Timeline message for invalid_account_number payout error.',
    defaultMessage:
      'Update the bank account number in Settings to continue receiving payouts.',
  },
  invalid_account_number_length: {
    id: 'mse_inline_data.merchant_payout_status.error.invalid_account_number_length',
    description:
      'Timeline message for invalid_account_number_length payout error.',
    defaultMessage:
      'Update the bank account number in Settings to continue receiving payouts.',
  },
  incorrect_account_holder_name: {
    id: 'mse_inline_data.merchant_payout_status.error.incorrect_account_holder_name',
    description:
      'Timeline message for incorrect_account_holder_name payout error.',
    defaultMessage:
      'Update your account holder name in Settings to continue receiving payouts.',
  },
  incorrect_account_holder_address: {
    id: 'mse_inline_data.merchant_payout_status.error.incorrect_account_holder_address',
    description:
      'Timeline message for incorrect_account_holder_address payout error.',
    defaultMessage:
      'Update your account holder address in Settings to continue receiving payouts.',
  },
  incorrect_account_holder_tax_id: {
    id: 'mse_inline_data.merchant_payout_status.error.incorrect_account_holder_tax_id',
    description:
      'Timeline message for incorrect_account_holder_tax_id payout error.',
    defaultMessage:
      "Update your account holder's Tax ID in Settings to continue receiving payouts.",
  },
  invalid_currency: {
    id: 'mse_inline_data.merchant_payout_status.error.invalid_currency',
    description: 'Timeline message for invalid_currency payout error.',
    defaultMessage: 'Contact your bank to start receiving payouts again.',
  },
  no_account: {
    id: 'mse_inline_data.merchant_payout_status.error.no_account',
    description: 'Timeline message for no_account payout error.',
    defaultMessage:
      'Update the bank account number in Settings to continue receiving payouts.',
  },
  stripe_could_not_process: {
    id: 'mse_inline_data.merchant_payout_status.error.stripe_could_not_process',
    description: 'Notice that a payout could not be processed by Stripe',
    defaultMessage: 'You can chat or speak with Stripe Support to learn more.',
  },
  unsupported_card: {
    id: 'mse_inline_data.merchant_payout_status.error.unsupported_card',
    description: 'Timeline message for unsupported_card payout error.',
    defaultMessage:
      'To receive payouts, switch to a different payment method or card.',
  },
  unknown: {
    id: 'mse_inline_data.merchant_payout_status.error.unknown',
    description: 'Timeline message for unknown payout error.',
    defaultMessage: 'You can chat or speak with Stripe Support to learn more.',
  },
});

const getFailedNextStepsId = (
  payout: Payout,
): TimelineErrorMessageKeys | undefined => {
  const destinationType = payout.destination?.type;
  const failureCode = payout.failure_code;

  if (destinationType === 'blockchain_address') {
    return undefined;
  }

  const bankPhoneNumber = payout.destination?.bank_phone_number;

  if (failureCode) {
    const apiFailureCode = failureCode as TimelineErrorMessageKeys;

    if (messages[apiFailureCode]) {
      if (!bankPhoneNumber) {
        const apiFailureCodeNoPhone =
          `${apiFailureCode}_no_phone` as TimelineErrorMessageKeys;

        if (messages[apiFailureCodeNoPhone]) {
          return apiFailureCodeNoPhone;
        }
      }

      return apiFailureCode;
    }
  }

  // undefined means that we will not show a specific next-step message.
  return undefined;
};

export const FailedNextStepsMessage = ({payout}: {payout: Payout}) => {
  const analytics = useAnalytics();

  const nextStepsId = getFailedNextStepsId(payout);
  useEffect(() => {
    analytics.viewed('uh_merchant_payout_status_failed_next_steps', {
      uh_merchant_payout_status_next_steps_id: nextStepsId,
    });
  }, [analytics, nextStepsId]);

  if (!nextStepsId) return <></>;

  return (
    <view.div
      data-testid={`payout-failed-next-steps-${nextStepsId}`}
      css={{font: 'label.medium'}}
    >
      <FormattedMessage
        {...messages[nextStepsId]}
        values={{
          phone: payout.destination?.bank_phone_number,
        }}
      />
    </view.div>
  );
};
